import { LoadingWheel } from "components/common/loading/loadingWheel";
import {
  createContext,
  ReactNode,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";

/** Grace period for components to register initial loading holds. */
const NO_HOLDS_TIMEOUT_MS = 1_000;

interface LoadingScreenContextType {
  /**
   * Register a loading hold. While any holds are active, the loading screen stays visible.
   * Returns a cleanup function to release the hold.
   */
  holdLoading: () => () => void;
  /**
   * Signal that the app is ready and no loading is needed.
   * If no holds are active, the loading screen hides immediately.
   */
  signalReady: () => void;
}

export const LoadingScreenContext =
  createContext<LoadingScreenContextType | null>(null);

/**
 * Hides the static loading screen. Immediate mode skips any in-progress fade
 * for synchronous captures.
 */
export function hideInitialLoadingScreen({
  immediate = false,
}: { immediate?: boolean } = {}) {
  const loadingScreen = document.getElementById("initial-loading-screen");
  if (!loadingScreen) {
    return;
  }
  if (immediate) {
    loadingScreen.classList.add("fade-out", "hidden");
    return;
  }
  if (!loadingScreen.classList.contains("fade-out")) {
    loadingScreen.classList.add("fade-out");
    setTimeout(() => {
      loadingScreen.classList.add("hidden");
    }, 500);
  }
}

interface LoadingScreenProviderProps {
  children: ReactNode;
}

/**
 * Keeps the static loading screen visible while holds are active. The screen
 * fades after the last release or after a grace period without a hold.
 */
export const LoadingScreenProvider = ({
  children,
}: LoadingScreenProviderProps) => {
  const [activeHolds, setActiveHolds] = useState(() => new Set());
  const hasHadHoldsRef = useRef(false);

  const holdLoading = useCallback(() => {
    const holdId = uuidv4();
    setActiveHolds((prev) => new Set(prev).add(holdId));

    return () => {
      setActiveHolds((prev) => {
        const next = new Set(prev);
        next.delete(holdId);
        return next;
      });
    };
  }, []);

  const isLoading = activeHolds.size > 0;

  const signalReady = useCallback(() => {
    if (!isLoading) {
      hideInitialLoadingScreen();
    }
  }, [isLoading]);

  const contextValue = useMemo(
    () => ({ holdLoading, signalReady }),
    [holdLoading, signalReady]
  );

  useEffect(() => {
    if (isLoading) {
      hasHadHoldsRef.current = true;
    }
    if (!isLoading && hasHadHoldsRef.current) {
      hideInitialLoadingScreen();
    }
  }, [isLoading]);

  useEffect(() => {
    const earlyTimer = setTimeout(() => {
      if (!hasHadHoldsRef.current) {
        hideInitialLoadingScreen();
      }
    }, NO_HOLDS_TIMEOUT_MS);

    return () => {
      clearTimeout(earlyTimer);
    };
  }, []);

  return (
    <LoadingScreenContext value={contextValue}>{children}</LoadingScreenContext>
  );
};

/**
 * Holds the global loading screen while mounted. The screen hides after the
 * last mounted instance releases its hold.
 */
export const GlobalLoadingScreen: React.FC = () => {
  const context = use(LoadingScreenContext);

  useEffect(() => {
    if (!context) return;
    return context.holdLoading();
  }, [context]);
  return <LoadingWheel />;
};

/** Hides the loading screen when no holds are active. */
export const useSignalReady = (): void => {
  const context = use(LoadingScreenContext);

  useEffect(() => {
    context?.signalReady();
  }, [context]);
};
